import { useCallback, useMemo } from "react";

import {
  type OrdersWidgetScope,
  OrdersWidget as RichOrdersWidget,
} from "~/v3-poc/features/orders";
import { ATD_WORKSPACE_ORDERS_POLLING_INTERVAL } from "~/v3-poc/features/orders/ordersRefresh";
import { matchesUnderlying } from "../../shared/symbolMatch";
import { useWorkspace } from "../../shared/workspace/Workspace.context";
import { getEffectiveWidgetSymbol } from "../../shared/workspace/workspace.utils";

export interface WorkspaceOrdersWidgetProps {
  id: string;
}

/**
 * Workspace-only adapter. It owns the id-based workspace contract and converts
 * the effective group symbol into the richer Orders feature's page-local scope.
 * The Orders feature owns account/query/filter/table/cancel behavior.
 */
export const WorkspaceOrdersWidget = ({ id }: WorkspaceOrdersWidgetProps) => {
  // Primitive selector — safe under Zustand v5 referential-equality bailout.
  const symbol = useWorkspace((s) => getEffectiveWidgetSymbol(s, id));

  const matchesOrderSymbol = useCallback(
    (orderSymbol: string) =>
      symbol ? matchesUnderlying(symbol, orderSymbol) : true,
    [symbol],
  );

  const scope = useMemo<OrdersWidgetScope | undefined>(
    () =>
      symbol
        ? {
            symbol,
            matchesOrderSymbol,
          }
        : undefined,
    [matchesOrderSymbol, symbol],
  );

  return (
    <RichOrdersWidget
      surface="workspace"
      scope={scope}
      defaultStatus="all"
      allowCancel
      pollingInterval={ATD_WORKSPACE_ORDERS_POLLING_INTERVAL}
    />
  );
};
